import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useCard } from '../../hooks/useCard';
import styles from './Card.module.css';
import SkeletonCard from '../SkeletonCard/SkeletonCard';

const Card = ({
    cardId,
    size = "Small",
    initialOpen = false,
    disableHover = false,
    autoPlay = true,
    hoverPlay = true,
    controls = false,
    loop = true,
    rotation = 0,
    onCardSelect,
    isControlledByGroup = false,
    setVideoRef = null  // Nueva prop para manejar la ref del video
}) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const [displayedSummary, setDisplayedSummary] = useState('');
    const [isTypingComplete, setIsTypingComplete] = useState(false);
    const videoRef = useRef(null);

    const {
        cardData,
        cursorSvg,
        isLoading,
        error
    } = useCard(cardId);

    // Pasar la ref del video al grupo si es necesario
    useEffect(() => {
        if (setVideoRef && videoRef.current) {
            setVideoRef(videoRef.current);
        }
    }, [setVideoRef]);

    useEffect(() => {
        if (isControlledByGroup) {
            setIsOpen(initialOpen);
        }
    }, [initialOpen, isControlledByGroup]);

    useEffect(() => {
        if (videoRef.current && autoPlay) {
            videoRef.current.play().catch(() => {});
        }
    }, [autoPlay]);

    useEffect(() => {
        if (isOpen && cardData?.summary) {
            setIsTypingComplete(false);
            let i = 0;
            const intervalId = setInterval(() => {
                setDisplayedSummary(cardData.summary.slice(0, i));
                i++;
                if (i > cardData.summary.length) {
                    clearInterval(intervalId);
                    setIsTypingComplete(true);
                }
            }, 20);
            
            return () => clearInterval(intervalId);
        } else {
            setDisplayedSummary('');
            setIsTypingComplete(false);
        }
    }, [isOpen, cardData]);

    const handleMouseEnter = useCallback(() => {
        if (!isControlledByGroup && hoverPlay && !disableHover) {
            setIsOpen(true);
            if (videoRef.current) {
                videoRef.current.play().catch(() => {});
            }
        }
    }, [hoverPlay, disableHover, isControlledByGroup]);

    const handleMouseLeave = useCallback(() => {
        if (!isControlledByGroup && hoverPlay && !disableHover) {
            setIsOpen(false);
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, [hoverPlay, disableHover, isControlledByGroup]);

    if (isLoading) {
        return (
            <SkeletonCard
                size={size}
                initialOpen={initialOpen}
                rotation={rotation}
            />
        );
    }

    if (error) {
        return (
            <div className={styles.cardError}>
                <p>❌ {error.message || 'Error loading card'}</p>
            </div>
        );
    }

    if (!cardData) {
        return null;
    }

    const cardClassName = `${styles.card} ${styles[size.toLowerCase()]} ${isOpen ? styles.open : styles.closed}`;

    const springTransition = {
        type: "spring",
        stiffness: 500,
        damping: 60,
        mass: 1,
        delay: 0,
    };

    return (
        <motion.div
            className={cardClassName}
            style={{
                backgroundColor: cardData.cardColor,
                transform: `rotate(${rotation}deg)`,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => {
                e.stopPropagation();
                if (onCardSelect) onCardSelect(cardId);
            }}
            initial={false}
            animate={{
                width: isOpen
                    ? size === "Grande" ? 362.4 : 311.92
                    : size === "Grande" ? 180 : 154.28,
            }}
            transition={springTransition}
        >
            <motion.div
                className={styles.videoContainer}
                style={cardData.videoBorderColor ? { border: `2px solid ${cardData.videoBorderColor}` } : {}}
                initial={false}
                animate={{
                    width: isOpen
                        ? size === "Grande" ? 342.4 : 295.92
                        : size === "Grande" ? 160 : 138.28,
                }}
                transition={springTransition}
            >
                <video
                    ref={videoRef}
                    className={styles.video}
                    src={isOpen ? cardData.videoopen : cardData.videoclosed}
                    autoPlay={autoPlay}
                    loop={loop}
                    muted
                    controls={controls}
                    playsInline
                />
            </motion.div>
            <div className={`${styles.mainContent} ${isOpen ? styles.open : styles.closed}`}>
                {isOpen && (
                    <div className={styles.logoContainer}>
                        <img src={cardData.logoimage} alt="Logo" />
                    </div>
                )}
                <div className={styles.companyInfo}>
                    <span className={`${styles.companyName} mulish`}>
                        {cardData.companyname}
                        {isOpen && (
                            <span className={styles.summaryText}>
                                , {displayedSummary}
                                {!isTypingComplete && cursorSvg && (
                                    <img
                                        src={cursorSvg}
                                        alt="cursor"
                                        className={styles.typingCursorSvg}
                                    />
                                )}
                            </span>
                        )}
                    </span>
                </div>
            </div>
        </motion.div>
    );
};

export default Card;